// src/components/Header.tsx
import React from 'react';
import styled, { css } from 'styled-components';
import { Container, Button, SecondaryButton as SharedSecondaryButton } from '../styles/layout';

const HeaderSection = styled.header`
  position: relative;
`;

const Intro = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 35vh;
  background: transparent;
`;

const Overlay = styled.div`
  padding: 140px 0 80px;
`;

const Row = styled.div`
  display: grid;
  justify-content: center;
  margin: 0 -15px;
`;

const IntroText = styled.div`
  max-width: 960px;
  padding: 0 15px;
  text-align: center;
  display: grid;
  gap: 18px;
`;

const Title = styled.h1`
  color: #1f2b3a;
  margin: 0;
`;

const TitleSpan = styled.span`
  font-weight: 500;
  color: #1c3650;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 6px;
`;

const Subtitle = styled.p`
  color: #4a5a6b;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.8;
  margin: 0 auto;
`;

const largeButton = css`
  height: 65px;
  padding: 16px 26px;
  border-radius: 16px;
`;

const CustomButton = styled(Button)`
  ${largeButton}
`;

const SecondaryButton = styled(SharedSecondaryButton)`
  ${largeButton}
`;

const CtaContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  img {
    width: 22px;
    height: 22px;
    flex: 0 0 auto;
    object-fit: contain;
  }
`;

const StoreBadge = styled.img`
  height: 82px;
  width: auto;
  display: block;
  border-radius: 12px;
`;

const StoreBadgeButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  // border: none;
  // border-radius: 16px;
  // box-shadow: 0 18px 34px rgba(12, 28, 45, 0.18);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover,
  &:focus {
    transform: translateY(-2px);
    // box-shadow: 0 24px 40px rgba(12, 28, 45, 0.24);
  }
`;

const StoreBadgeDisabled = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  // border-radius: 16px;
  // box-shadow: 0 12px 26px rgba(12, 28, 45, 0.12);
  filter: grayscale(1);
  opacity: 0.3;
  cursor: not-allowed;
`;

const DisabledButton = styled.span`
  text-transform: none;
  color: rgba(15, 28, 45, 0.6);
  background: rgba(255, 255, 255, 0.6);
  padding: 16px 26px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 16px;
  border: 1px solid rgba(12, 28, 45, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 26px rgba(12, 28, 45, 0.12);
  cursor: not-allowed;
`;

interface HeaderProps {
  data?: {
    title: string;
    paragraph: string;
    primaryCtaLabel?: string;
    primaryCtaHref?: string;
    primaryCtaDisabled?: boolean;
    primaryCtaIcon?: 'google-play' | 'apple';
    secondaryCtaLabel?: string;
    secondaryCtaHref?: string;
    secondaryCtaDisabled?: boolean;
    secondaryCtaIcon?: 'google-play' | 'apple';
  };
}

const renderCtaIcon = (icon?: 'google-play' | 'apple', label?: string) => {
  if (icon === 'apple') {
    return (
      <StoreBadge
        src={require('../assets/images/stores/GetItOnAppleStore.png')}
        alt={label || 'Apple App Store'}
      />
    );
  }

  if (icon === 'google-play') {
    return (
      <StoreBadge
        src={require('../assets/images/stores/GetItOnGooglePlay.png')}
        alt={label || 'Google Play'}
      />
    );
  }

  return null;
};

const ProductHeader: React.FC<HeaderProps> = (props) => {
  return (
    <HeaderSection id="header">
      <Intro>
        <Overlay>
          <Container>
            <Row>
              <IntroText>
                <Title>
                  {props.data ? props.data.title : 'Loading'}
                  <TitleSpan></TitleSpan>
                </Title>
                <Subtitle>{props.data ? props.data.paragraph : 'Loading'}</Subtitle>
                {(props.data?.primaryCtaLabel || props.data?.secondaryCtaLabel) && (
                  <ButtonRow>
                    {props.data?.primaryCtaLabel &&
                      (props.data.primaryCtaHref || props.data.primaryCtaDisabled) &&
                      (props.data.primaryCtaIcon ? (
                        props.data.primaryCtaDisabled ? (
                          <StoreBadgeDisabled>
                            {renderCtaIcon(
                              props.data.primaryCtaIcon,
                              props.data.primaryCtaLabel
                            )}
                          </StoreBadgeDisabled>
                        ) : (
                          <StoreBadgeButton
                            href={props.data.primaryCtaHref}
                            aria-label={props.data.primaryCtaLabel}
                          >
                            {renderCtaIcon(
                              props.data.primaryCtaIcon,
                              props.data.primaryCtaLabel
                            )}
                          </StoreBadgeButton>
                        )
                      ) : props.data.primaryCtaDisabled ? (
                        <DisabledButton>{props.data.primaryCtaLabel}</DisabledButton>
                      ) : (
                        <CustomButton href={props.data.primaryCtaHref}>
                          <CtaContent>
                            <span>{props.data.primaryCtaLabel}</span>
                          </CtaContent>
                        </CustomButton>
                      ))}
                    {props.data?.secondaryCtaLabel &&
                      (props.data.secondaryCtaHref || props.data.secondaryCtaDisabled) &&
                      (props.data.secondaryCtaIcon ? (
                        props.data.secondaryCtaDisabled ? (
                          <StoreBadgeDisabled>
                            {renderCtaIcon(
                              props.data.secondaryCtaIcon,
                              props.data.secondaryCtaLabel
                            )}
                          </StoreBadgeDisabled>
                        ) : (
                          <StoreBadgeButton
                            href={props.data.secondaryCtaHref}
                            aria-label={props.data.secondaryCtaLabel}
                          >
                            {renderCtaIcon(
                              props.data.secondaryCtaIcon,
                              props.data.secondaryCtaLabel
                            )}
                          </StoreBadgeButton>
                        )
                      ) : props.data.secondaryCtaDisabled ? (
                        <DisabledButton>{props.data.secondaryCtaLabel}</DisabledButton>
                      ) : (
                        <SecondaryButton href={props.data.secondaryCtaHref}>
                          <CtaContent>
                            <span>{props.data.secondaryCtaLabel}</span>
                          </CtaContent>
                        </SecondaryButton>
                      ))}
                  </ButtonRow>
                )}
              </IntroText>
            </Row>
          </Container>
        </Overlay>
      </Intro>
    </HeaderSection>
  );
};

export default ProductHeader;
